import {ItemOption} from "@eshop/types/catalogue.types.ts";
import React, { forwardRef, JSX, useEffect, useImperativeHandle, useState } from 'react';
import dayjs, {Dayjs} from "dayjs";
import localeData from 'dayjs/plugin/localeData.js';
import {logInfo} from "@tools/LogsHandler.tsx";
import {
  Calendar,
  Col,
  Form,
  FormInstance,
  Input,
  message,
  Row,
  Select,
  TimePicker
} from "antd";
import {CalendarMode} from "antd/es/calendar/generateCalendar";
import { DictValues } from '@tools/utils/common.tsx';
import { HorariosDisponiblesType } from '@store/slices/eshopSlice.tsx';


/*****  CUSTOM INPUTS *****/

dayjs.extend(localeData);

interface CalendarHeaderProps {
  value: Dayjs;
  type: CalendarMode;
  onChange: (value: Dayjs) => void;
  onTypeChange: (type: CalendarMode) => void;
}

const CustomCalendarHeader: React.FC<CalendarHeaderProps> = ({ value, onChange, /* type, onTypeChange */ }) => {
  const start = 0;
  const end = 12;
  const monthOptions: JSX.Element[] = [];

  let current = value.clone();
  const localeData = value.localeData();
  const months = [];
  for (let i = 0; i < 12; i++) {
    current = current.month(i);
    months.push(localeData.monthsShort(current));
  }

  for (let i = start; i < end; i++) {
    monthOptions.push(
      <Select.Option key={i} value={i} className="month-item">
        {months[i]}
      </Select.Option>,
    );
  }

  const year = value.year();
  const month = value.month();
  const options = [];
  for (let i = year - 10; i < year + 10; i += 1) {
    options.push(
      <Select.Option key={i} value={i} className="year-item">
        {i}
      </Select.Option>,
    );
  }

  return (
    <div style={{ padding: 8 }}>
      <Row gutter={8}>
        <Col>
          <Select
            size="small"
            popupMatchSelectWidth={false}
            className="my-year-select"
            value={year}
            onChange={(newYear) => {
              const now = value.clone().year(newYear);
              onChange(now);
            }}
            // required={false}
          >
            {options}
          </Select>
        </Col>
        <Col>
          <Select
            size="small"
            popupMatchSelectWidth={false}
            value={month}
            onChange={(newMonth) => {
              const now = value.clone().month(newMonth);
              onChange(now);
            }}
            // required={false}
          >
            {monthOptions}
          </Select>
        </Col>
      </Row>
    </div>
  );
};

interface OptionComponentFormProps {
  op: ItemOption;
  dictValues: DictValues;
  limitDate?: boolean;
  days?: number;
  horarios?: HorariosDisponiblesType;
}
export const CalendarInput: React.FC<OptionComponentFormProps> = ({ op, dictValues = {}, limitDate = false, days = 0, horarios = {} }) => {
  const initialValue = dictValues[op.code] ? dayjs(dictValues[op.code]) : dayjs();
  const [dateValue, setDateValue] = useState<Dayjs>(initialValue);

  const onPanelChange = (value: Dayjs, mode: string) => {
    logInfo('onPanelChange - ', value.format('YYYY-MM-DD'), mode);
  };

  const handleDateSelect = (date: Dayjs) => {
    setDateValue(date);
    dictValues[op.code] = date.format('YYYY-MM-DD');  // Corrección para asignar la fecha formateada al objeto options
  };

  const disabledDate = (current: Dayjs) => {
    if (!current) return false;
    const dayOfWeek = current.day();
    const availableHours = horarios[dayOfWeek];
    if (limitDate) {
      const startDay = dayjs().add(days, 'day').startOf('day');
      return current < startDay || availableHours.length === 0;
    } else {
      return availableHours.length === 0;
    }
  };

  return (
    <div style={{ width: 300, border: '1px solid #d9d9d9', borderRadius: 4 }}>
      <Input type='date' value={dateValue.format('YYYY-MM-DD')} required={op.type.toLowerCase() === 'required'} className="d-none" />
      <Form.Item
        name={`${op.code}`}
        initialValue={dateValue}
        rules={[{ required: op.type.toLowerCase() === 'required', message: `${op.name} es requerido.` }]}
      >
        <Calendar
          fullscreen={false}
          onPanelChange={onPanelChange}
          onSelect={handleDateSelect}
          mode={'month'}
          value={dateValue}
          disabledDate={disabledDate}
        />
      </Form.Item>
    </div>
  );
};


interface CalendarTimeInputProps {
  op: ItemOption;
  dictValues: DictValues;
  limitDate?: boolean;
  days?: number;
  form: FormInstance;
  horarios?: HorariosDisponiblesType;
}

export const CalendarTimeInput = forwardRef<unknown, CalendarTimeInputProps>(({ op, dictValues = {}, limitDate = false, days = 0, form, horarios = {} }, ref) => {
  const initialDateTime = dictValues[op.code] ? dayjs(dictValues[op.code]) : undefined;
  const initialDate = initialDateTime ? initialDateTime.startOf('day') : undefined;
  const initialTime = initialDateTime ? initialDateTime : undefined;

  const [dateValue, setDateValue] = useState<Dayjs | undefined>(initialDate);
  const [timeValue, setTimeValue] = useState<Dayjs | undefined>(initialTime);


  logInfo('initialDateTime:', initialDateTime, 'initialDate:', initialDate,
    'initialTime: ', initialTime, 'dateValue: ', dateValue, 'timeValue: ', timeValue);
  const handleDateSelect = (date: Dayjs) => {
    setDateValue(date);
  };

  const handleTimeChange = (time: Dayjs | null) => {
    setTimeValue(time ?? undefined);
    // setError(null);  // Limpiar error al seleccionar hora
  };

  const updateDictValues = (date: Dayjs | undefined, time: Dayjs | undefined) => {
    if (date && time) {
      const dateTimeValue = date.hour(time.hour()).minute(time.minute());
      dictValues[op.code] = dateTimeValue.format('YYYY-MM-DDTHH:mm');
      form.setFieldsValue({ [op.code]: dictValues[op.code] });
      logInfo('Se actualizó dictValues[op.code]: ', dictValues[op.code]);
    } else {
      dictValues[op.code] = undefined;
      form.setFieldsValue({ [op.code]: '' });
    }
  };

  const disabledDate = (current: Dayjs) => {
    if (!current) return false;
    const dayOfWeek = current.day();
    const availableHours = horarios[dayOfWeek];
    if (limitDate) {
      const startDay = dayjs().add(days, 'day').startOf('day');
      return current < startDay || !availableHours || availableHours.length === 0;
    } else {
      return availableHours.length === 0;
    }
  };

  const disabledTime = (current: Dayjs | null) => {
    if (!dateValue) return {};
    const dayOfWeek = dateValue.day();
    logInfo('horarios:', horarios, 'dayOfWeek:', dayOfWeek,
      'horarios[dayOfWeek]: ', horarios[dayOfWeek], 'current: ', current);
    const availableHours = horarios[dayOfWeek];

    const disabledHours = () => {
      const hours = [];
      for (let i = 0; i < 24; i++) {
        let isEnabled = false;
        for (const range of availableHours) {
          if (i >= range[0] && i < range[1]) {
            isEnabled = true;
            break;
          }
        }
        if (!isEnabled) {
          hours.push(i);
        }
      }
      return hours;
    };

    return {
      disabledHours,
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  useEffect(() => {
    if (dateValue) {
      const dayOfWeek = dateValue.day();
      const availableHours = horarios[dayOfWeek];

      let isValidTime = false;
      for (const range of availableHours) {
        if (timeValue && timeValue.hour() >= range[0] && timeValue.hour() < range[1]) {
          isValidTime = true;
          break;
        }
      }
      if (timeValue && !isValidTime) {
        setTimeValue(undefined);
        form.setFieldsValue({ [`${op.code}_time`]: undefined });
        message.error(`El horario seleccionado está fuera del rango permitido para este día.`);
      }

      updateDictValues(dateValue, timeValue);
      logInfo('CalendarTimeInput - dateValue: ', dateValue, '\ntimeValue: ', timeValue);
    }
  }, [dateValue, timeValue]);   // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    validate: () => {
      logInfo('op.type: ', op, 'Validate - dateValue: ', dateValue, '\ntimeValue: ', timeValue);
      if (op.type.toLowerCase() === 'required' && (!dateValue || !timeValue)) {
        message.error(`${op.name} es requerido.`);
        return false;
      }
      return true;
    }
  }));

  return (
    <div style={{ width: 300, border: '1px solid #d9d9d9', borderRadius: 4, padding: 16 }}>
      <Form.Item
        name={`${op.code}_date`}
        initialValue={dateValue}
        rules={[{ required: op.type.toLowerCase() === 'required', message: `${op.name} es requerido.` }]}
      >
        <Calendar
          headerRender={(props) => <CustomCalendarHeader {...props} />}
          fullscreen={false}
          value={dateValue}
          onSelect={handleDateSelect}
          disabledDate={disabledDate}
        />
      </Form.Item>
      <Form.Item
        name={`${op.code}_time`}
        initialValue={timeValue}
        rules={[{ required: op.type.toLowerCase() === 'required', message: `${op.name} es requerido.` }]}
      >
        <TimePicker
          value={timeValue}
          disabledTime={disabledTime}
          onChange={handleTimeChange}
          format="HH:mm"
          minuteStep={15}
          hideDisabledOptions={true}
          showNow={false}
          needConfirm={false}
          style={{ width: '100%', marginTop: 8 }}
        />
      </Form.Item>
      <Form.Item
        className="d-none"
        name={op.code}
        rules={[{ required: op.type.toLowerCase() === 'required' }]}
      >
        <Input type='hidden'
               value={dictValues[op.code]}
               readOnly
               required={op.type.toLowerCase() === 'required'} />
      </Form.Item>
    </div>
  );
});

CalendarTimeInput.displayName = 'CalendarTimeInput';
